import React from 'react';
import {
  Box,
  ExpandableSection,
  SpaceBetween,
  StatusIndicator,
} from '@cloudscape-design/components';
import { CHECK_ANCHOR } from './navigation';
import { CATEGORY_STATE, HUMAN_VERIFY_ROLE } from './reviewRecord';
import EvidenceView from './EvidenceView';
import { showsDetails } from './checkEvidence';
import { Marked } from './placeholder';

/**
 * Display pieces shared by ISFR's Approval Summary and its category tabs: how a
 * category state and a check result read, and how one check renders.
 */

export const preWrap = { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' };

export { Marked, PLACEHOLDER } from './placeholder';

/** A category state as a status indicator, a short text and a tab-label icon. */
export const categoryStatus = (category) => {
  switch (category.state) {
    case CATEGORY_STATE.PASS:
      return { type: 'success', text: 'Passed', icon: '✅' };
    case CATEGORY_STATE.FAILED:
      return { type: 'error', text: 'Not clear', icon: '❌' };
    case CATEGORY_STATE.PENDING:
      return {
        type: 'pending',
        text: category.role === HUMAN_VERIFY_ROLE ? 'Pending your human attention' : 'Pending',
        icon: '⏳',
      };
    case CATEGORY_STATE.NOT_REVIEWED:
      return {
        type: 'warning',
        text: 'Not reviewed (nothing reported, so it cannot count as clear)',
        icon: '⚠️',
      };
    case CATEGORY_STATE.NOT_APPLICABLE:
      return { type: 'stopped', text: "Not applicable at this request's tier", icon: '➖' };
    default:
      return { type: 'warning', text: `Unrecognised state: ${category.state || 'none'}`, icon: '⚠️' };
  }
};

// A deferred or unverified check did not complete, so it must never read as a
// pass. Inside human verification a deferral is the expected data-gated state,
// so it is worded as the reviewer's job rather than as a gap. In an advisory
// category the agent's result assists the reviewer, who verifies the item
// either way, so it never reads as a pass or a finding.
/** What the agent's review found, for a result in an advisory category. */
export const agentReviewText = (result) => `Agent's review ${
  { PASS: 'found no issue', FAIL: 'found a problem', FLAG: 'raised a point' }[result] || `reported ${result || 'no result'}`}`;

export const checkStatus = (result, role, advisory = false) => {
  if (advisory && ['PASS', 'FAIL', 'FLAG'].includes(result)) {
    return { type: 'info', text: `${agentReviewText(result)} · yours to verify` };
  }
  switch (result) {
    case 'PASS':
      return { type: 'success', text: 'Passed' };
    case 'FAIL':
      return { type: 'error', text: 'Failed' };
    case 'FLAG':
      return { type: 'warning', text: 'Flagged' };
    case 'ERROR':
      return { type: 'error', text: 'Did not complete' };
    case 'PARTIAL':
      return { type: 'warning', text: 'Partially assessed, not a pass' };
    case 'UNVERIFIED':
      return { type: 'stopped', text: 'Unverified, not a pass' };
    case 'DEFERRED':
      return role === HUMAN_VERIFY_ROLE
        ? { type: 'pending', text: 'Verify by hand' }
        : { type: 'stopped', text: 'Deferred, not a pass' };
    default:
      return { type: 'info', text: result || 'No result' };
  }
};

export const BulletList = ({ items }) => (
  <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
    {items.map((item, i) => (
      <li key={i} style={{ marginBottom: '6px', ...preWrap }}><Marked text={item} /></li>
    ))}
  </ul>
);

// The submission phrases the agent cited as its evidence, shown exactly as
// stored and never paraphrased, so a reviewer can check the reasoning against
// them. Nothing yet confirms them against the submission, so they are labelled
// as the agent's citations.
export const EvidenceQuotes = ({ quotes }) => (
  <ExpandableSection
    variant="footer"
    headerText={`Cited from the submission (${quotes.length})`}
  >
    <SpaceBetween size="xs">
      <Box variant="small" color="text-body-secondary">
        The agent cites these as the submission&apos;s own words. They are not yet
        checked against the submission.
      </Box>
      {quotes.map((quote, i) => (
        <blockquote
          key={i}
          data-testid="isfr-evidence-quote"
          style={{
            ...preWrap,
            margin: 0,
            padding: '4px 12px',
            borderLeft: '3px solid #d5dbdb',
            fontStyle: 'italic',
          }}
        >
          {quote}
        </blockquote>
      ))}
    </SpaceBetween>
  </ExpandableSection>
);

/** A check's name for display. The agent's check id is never shown. */
export const checkName = (check) => (check && check.name ? check.name : 'Unnamed check');

/**
 * One check: its name, what its result means, any context the caller adds,
 * its one-line details, then what it worked from and concluded (its evidence)
 * and any quotes it cited. reasonShown leaves out why it could not run, when the
 * view already says so above it.
 */
export const CheckRow = ({
  check, role, advisory, statusText, nameOf, context, reasonShown,
}) => {
  const status = checkStatus(check.result, role, advisory);
  return (
    <div {...{ [CHECK_ANCHOR]: check.id }}>
      <StatusIndicator type={statusText ? 'stopped' : status.type}>
        <Box variant="strong" display="inline">{checkName(check)}</Box>
        {` · ${statusText || status.text}`}
      </StatusIndicator>
      <div style={{ paddingLeft: '20px', paddingTop: '4px' }}>
        <SpaceBetween size="xs">
          {context}
          {check.details && showsDetails(check.id, check.evidence) && (
            <Box variant="p" color="text-body-secondary"><span style={preWrap}><Marked text={check.details} /></span></Box>
          )}
          <EvidenceView check={check} nameOf={nameOf} omitReason={reasonShown} />
          {check.evidenceQuotes && check.evidenceQuotes.length > 0 && (
            <EvidenceQuotes quotes={check.evidenceQuotes} />
          )}
        </SpaceBetween>
      </div>
    </div>
  );
};

export const NOT_APPLICABLE_TEXT = "Not applicable at this request's tier";

/**
 * Why a view of per-check detail is empty, by the reviewer package's read
 * status. Records validated before the agent saved per-check results carry
 * none, and a request validated again on an agent that saves them gets them.
 */
export const checksNotSavedText = (status) => (status === 'unreadable'
  ? "This request's check-by-check results could not be read."
  : "Check-by-check results aren't saved for this request. Requests validated since the agent started saving them show every check.");
